<script>
    import table from './ib-table-basic.vue'

    const controlWidths = {
        check: '40px',
        toggle: '64px'
    };

    /**
     * Extension of [ib-table-basic](#ibtablebasic) with selectable rows.
     */
    export default {
        name: 'ib-table-select',
        mixins: [table],

        props: {
            /**
             * DOM `name` attribute; used as property in enclosing `ib-form`.
             */
            name: String,
            /**
             * An array of currently selected table rows by id.
             */
            value: [String,Number,Array],
            /**
             * If `true`, allow multiple row selection.
             */
            multiple: Boolean,
            /**
             * If `true`, disable selection changes.
             */
            disabled: Boolean,
            /**
             * If `true`, the form entry state will be invalid unless a row is selected.
             */
            required: Boolean,
            /**
             * Optional selection control: `check` and `toggle` are supported.
             */
            control: String,
            /**
             * Selection control position. `start` is supported; default is `end`.
             */
            controlPosition: String,
            /**
             * Click action. `none` is supported; default is `select` to select current row.
             */
            click: String,
            /**
             * If `true`, highlight selected rows with a selection background color.
             */
            highlight: Boolean,
            /**
             * Sets an error message for the table.
             */
            error: String,
            errorMissing: String
        },

        data() {
            return {
                selected: []
            };
        },

        computed: {
            selectAll: {
                get() {
                    return (this.selected.length == this.data.length);
                },
                set(value) {
                    this.selected = value ? this.indexed.map(tr => tr.id) : [];
                    this.$emit('input', this.selected.filter(i => this.v[i]));
                }
            },

            lclick() {
                return this.click != 'none';
            },

            controlComponent() {
                return {
                    check: this.multiple ? 'ib-checkbox' : 'ib-radio',
                    toggle: 'ib-toggle'
                }[this.control];
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (value == null) {
                        this.selected = [];
                    } else {
                        if (value instanceof Array) {
                            this.selected = value.slice();
                        } else {
                            this.selected = [value];
                        }
                    }
                },
                immediate: true
            },

            v(visible) {
                const sel = this.selected.filter((i) => visible[i]);
                if (sel.length != this.selected.length) {
                    this.$emit('input', (this.selected = sel).slice());
                }
            }
        },

        methods: {
            renderBefore(h) {
                return this.control ? [] : [
                    h('ib-form-entry', {
                        'attrs': { 'name': this.name || '_tb' + this._uid, 'value': this.selected }
                    })
                ];
            },

            renderControlCol(h) {
                return [
                    h('ib-table-col', {
                        'class': this.controlComponent ? null : '_tbsid',
                        'attrs': {
                            sticky: 'start',
                            width: controlWidths[this.control] || '8px',
                            force: true
                        }
                    }, this.control == 'check' && this.multiple ? [
                        h('ib-checkbox', {
                            'attrs': { disabled: this.disabled },
                            'model': {
                                value: this.selectAll,
                                callback: value => this.selectAll = value,
                                expression: 'selectAll'
                            }
                        })] : void 0
                    )];
            },

            renderHeadBefore(h) {
                return !this.controlComponent || this.controlPosition === 'start' ? this.renderControlCol(h) : [];
            },

            renderHeadAfter(h) {
                return this.controlComponent && this.controlPosition !== 'start' ? this.renderControlCol(h) : [];
            },

            renderControl(h, id) {
                return [h('td', {
                    }, [
                        h(this.controlComponent, {
                            'ref': 'sel' + id,
                            'class': this.click == 'none' ? null : '_tbpn',
                            'attrs': {
                                name: this.name || '_tb' + this._uid,
                                val: id,
                                required: this.required,
                                disabled: this.disabled,
                                error: this.error,
                                errorMissing: this.errorMissing
                            },
                            'model': {
                                value: this.selected,
                                callback: value => this.selected = value,
                                expression: 'selected'
                            }
                        })
                    ])
                ];
            },

            renderBody(h) {
                const body = this.renderSlot('body');
                const slot = this.slotStart || this.slotEnd;
                const ulid = this.ulid;
                const utr = !!this.utr;
                const rh = this.rowhover;
                let highlight = {
                    check: this.highlight,
                    toggle: this.highlight
                }[this.control];
                if (highlight === void 0) {
                    highlight = true;
                }

                const bodyBefore = this.controlComponent
                    ? this.controlPosition === 'start'
                        ? this.renderControl
                        : h => []
                    : h => [h('td', {
                            'staticClass': '_tbsid'
                        }, [
                            h('div', { staticClass: '_tbst' })
                        ])
                    ];

                const bodyAfter = this.controlComponent && this.controlPosition !== 'start'
                    ? this.renderControl
                    : h => [];

                return h('tbody', {
                        'ref': 'body',
                        'staticClass': '_tbb'
                    },
                    this.sorted.length
                        ? this.sorted.map((row,index) => h('tr', {
                                'style': this.v[row.id] ? row.id === ulid ? 'transform:translateX(' + -this.uloff + 'px)' : null : 'display:none',
                                'class': {
                                    _trgt: slot,
                                    _trh: index === rh,
                                    _tro: row.id === ulid,
                                    _tui: row.id === ulid && inplace,
                                    _trs: row.id === ulid && utr,
                                    _act: highlight && this.selected.includes(row.id),
                                    _da: this.disabled
                                }
                            }, [
                                ...this.renderBodyStart(h, row),
                                ...bodyBefore(h, row.id),
                                body(row.data),
                                ...bodyAfter(h, row.id),
                                ...this.renderBodyEnd(h, row)
                            ]))
                        : this.renderEmpty(h));
            },

            input(value) {
                if (this.multiple) {
                    this.$emit('input', value.filter(id => this.v[id]));
                } else {
                    this.$emit('input', value[0]);
                }
            },

            clickhook(ri) {
                if (ri >= 0 && !this.disabled) {
                    const rid = this.sorted[ri].id;
                    if (this.multiple) {
                        if (this.selected.includes(this.sorted[ri].id)) {
                            this.selected = this.selected.filter(id => id != rid);
                        } else {
                            this.selected.push(rid);
                        }
                    } else {
                        this.selected = [rid];
                    }
                    this.input(this.selected);
                }
            },

            before() {
                const start = { width: 0 };
                if (!this.controlComponent || this.controlPosition === 'start') {
                    const controlWidth = controlWidths[this.control] || '8px';
                    const check = { width: controlWidth, sticky: 'start', force: 'true', align: 'center' };
                    return this.stickyStart >= 0 ? [start, check] : [check];
                } else {
                    return this.stickyStart >= 0 ? [start] : [];
                }
            },

            after() {
                const end = { width: 0 };
                if (this.controlComponent && this.controlPosition !== 'start') {
                    const controlWidth = controlWidths[this.control] || '8px';
                    const check = { width: controlWidth, sticky: 'end', force: 'true', align: 'center' };
                    return this.stickyEnd >= 0 ? [check, end] : [check];
                } else {
                    return this.stickyEnd >= 0 ? [end] : [];
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tbw {
        &._sr {
            tbody {
                tr {
                    cursor: pointer;
                    -webkit-user-select: none;
                       -moz-user-select: none;
                        -ms-user-select: none;
                            user-select: none;
                    &:hover input + span {
                        border: 1px solid @fg-accent;
                    }
                }
            }
        }
    }
    tr._act {
        td {
            background-color: mix(@bg-accent, @bg, @table-active);
        }
        ._tbst {
            transform: scaleX(1);
        }
    }
    ._tbbtransparent tr._act {
        td {
            background-color: fade(@bg-accent, @table-active);
        }
    }
    ._tbsid {
        padding: 0 !important;
        text-align: center;
    }
    ._tbpn {
        pointer-events: none;
    }
    ._tbst {
        position: absolute;
        top: 0;
        height: 100%;
        border-left: 3px solid @fg-accent;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform .25s;
    }
    [dir=rtl] ._tbst {
        transform-origin: right;
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() { return {
                sample1: [
                    { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                    { currency: 'GBP', amount: 35, note: 'Thirty-five British pounts' },
                    { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                    { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                    { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                    { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                    { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                    { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                    { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                ],
                value1: null,
                value2: []
            }},
            template:
                `<ib-context>
                    <p class="after-md">Single select: {{value1}}</p>
                    <ib-table-select :data="sample1" v-model="value1" emit="currency">
                        <ib-table-col width="8ch" sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col width="24ch">Note</ib-table-col>
                        <ib-table-col width="12ch" sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-select>

                    <p class="before-xl after-md">Multiple select: {{value2}}</p>
                    <ib-table-select :data="sample1" v-model="value2" emit="currency" multiple>
                        <ib-table-col width="8ch" sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col width="24ch">Note</ib-table-col>
                        <ib-table-col width="12ch" sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-select>
                </ib-context>`
        });
    ```
</docs>
